import {
  useCallback,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import styled, { css, type DefaultTheme } from "styled-components";

import { Text, type TextVariantDefinition } from "../Text";
import { Tooltip, type TooltipPlacement } from "../Tooltip";
import { TruncatedText } from "../TruncatedText";
import {
  inlineElementHeight,
  inlineElementIconSize,
  inlineElementTypography,
} from "../foundations/controlStyles";
import {
  interactiveSurfaceCss,
  type InteractiveSurfaceSpec,
} from "../foundations/interactiveSurface";
import { isRenderableNode } from "../../utils/reactNode";
import { useDevWarnOnce } from "../../utils/useDevWarnOnce";

/** SearchInput InputSize 와 통일한 사이즈 스케일. sm 은 dense filter bar, md 는 기본. */
export type ContentSwitcherSize = "sm" | "md";

/**
 * 세그먼트 묶음의 가로 폭 모드. (RFC-filter-search-primitives §width 가드)
 * - `intrinsic`: 내용 폭에 맞춰 줄어듦(기본). 세그먼트는 각자 라벨 폭.
 * - `expanded`: 컨테이너 폭을 채우고 세그먼트가 균등 분할.
 * - `fixed`: `value`(px) 고정 폭. 넘치면 세그먼트 라벨이 ellipsis되고, 명시 Tooltip이 없는
 *   라벨은 `TruncationTooltipHost`를 통해 전체 값을 복원한다(버튼 자체가 잘릴 수 있음).
 */
export type LayoutWidth =
  | { type: "intrinsic" }
  | { type: "expanded" }
  | { type: "fixed"; value: number };

/**
 * 세그먼트 하나의 계약. 보이는 내용은 채워진 필드가 정한다(presence 기반):
 * - `label`만 → 텍스트 세그먼트(현행).
 * - `icon`만 → 아이콘 세그먼트. 보이는 텍스트가 없으므로 `ariaLabel`이 접근성 이름이다(필수).
 * - `icon` + `label` → 아이콘-라벨 세그먼트(아이콘 뒤에 텍스트).
 *
 * `Dropdown`의 `SelectOption`(항상 `label` 필수)과 달리 ContentSwitcher는 아이콘 전용
 * 세그먼트를 허용하므로 `label`을 optional로 두고 별도 타입으로 분리한다.
 */
export type ContentSwitcherOption = {
  value: string;
  /** 세그먼트에 보이는 텍스트. 아이콘 전용 세그먼트는 생략한다. */
  label?: string;
  /** 세그먼트에 보이는 아이콘(`ReactNode`). `label`과 함께 주면 아이콘-라벨 형태. */
  icon?: ReactNode;
  /** 아이콘 전용(보이는 라벨 없음) 세그먼트의 접근성 이름 + hover 툴팁 내용. 없으면 devWarn. */
  ariaLabel?: string;
  /** 세그먼트의 기능을 설명하는 hover/focus c6 Tooltip 내용. */
  tooltip?: ReactNode;
  /** disabled 상태에서 `tooltip` 대신 표시할 사유. */
  disabledTooltip?: ReactNode;
  disabled?: boolean;
};

export type ContentSwitcherProps = {
  /** Accessible name for the segmented group. */
  label: string;
  options: readonly ContentSwitcherOption[];
  /** The single selected value. Exactly one segment is active at a time. */
  value: string | null;
  onValueChange: (value: string) => void;
  /** Fixed segment sizing. Default `"md"`. */
  size?: ContentSwitcherSize;
  /** 가로 폭 모드. 기본 `{ type: "intrinsic" }`(내용 폭). */
  width?: LayoutWidth;
  /**
   * 옵션별 hover/focus 툴팁의 방향. 기본 `"bottom"`(TooltipIconButton 과 동일 —
   * 툴바성 컨트롤은 아래로 띄워 위 콘텐츠를 가리지 않는다). 뷰포트 가장자리에선 Tooltip 이 auto-flip.
   */
  tooltipPlacement?: TooltipPlacement;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
};

type SegmentSizeStyle = {
  groupHeight: string;
  paddingX: string;
  fontSize: string;
  // 아이콘-라벨 세그먼트에서 아이콘과 텍스트 사이 간격. 단일 자식(텍스트만/아이콘만)이면 무효.
  gap: string;
};

// 사이즈는 Group 외곽 높이(box-sizing: border-box)로 고정하고, 세그먼트는 stretch + center 로
// 채운다(상하 패딩 대신). 폭은 width 모드가 정한다. 높이는 공용 inlineElementHeight(입력/IconButton/
// SearchInput 공유)에서 소싱 — dense filter bar 에서 컨트롤들이 한 줄 높이로 정렬된다.
const segmentSizeStyles: Record<ContentSwitcherSize, SegmentSizeStyle> = {
  sm: {
    groupHeight: inlineElementHeight.sm,
    paddingX: "0.5rem",
    fontSize: inlineElementTypography.sm.fontSize,
    gap: "0.32rem",
  },
  md: {
    groupHeight: inlineElementHeight.md,
    paddingX: "0.7rem",
    fontSize: inlineElementTypography.md.fontSize,
    gap: "0.4rem",
  },
};

// font-size 는 Segment($size)가 정하고, 텍스트는 자르지 않고 한 줄로 전부 보인다.
const segmentTextVariant = {
  as: "span",
  style: {
    display: "block",
    color: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
    whiteSpace: "nowrap",
  },
} satisfies TextVariantDefinition;

// fixed 폭의 명시 Tooltip 옵션은 그 authored description/disabled reason을 우선한다. 잘림 CSS만
// 유지하고 TruncatedText marker는 달지 않아 한 요소에서 두 Tooltip이 경쟁하지 않게 한다.
const segmentEllipsisTextVariant = {
  as: "span",
  style: {
    ...segmentTextVariant.style,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
} satisfies TextVariantDefinition;

// fixed 폭이면서 명시 Tooltip이 없는 라벨은 공용 TruncatedText marker를 달아 실제 overflow일 때만
// 전체 값을 복원한다. 그 외 폭 모드는 segmentTextVariant라 잘림 자체가 없다.
const segmentTruncateTextVariant = {
  ...segmentEllipsisTextVariant,
  as: TruncatedText,
} satisfies TextVariantDefinition;

// SWD-647: 트랙은 surface + subtle border의 조용한 context frame으로 둔다. 1px inset/gap은
// 기존 outer box를 유지하면서 세그먼트 면을 넓히고 이웃 경계만 남긴다. $layout 에 따라 폭이
// 정해진다: intrinsic=내용 폭, expanded=컨테이너 채움, fixed=고정 px.
const Group = styled.div<{
  $size: ContentSwitcherSize;
  $layout: LayoutWidth["type"];
  $fixedWidth: number | null;
}>`
  box-sizing: border-box;
  display: ${({ $layout }) => ($layout === "intrinsic" ? "inline-flex" : "flex")};
  align-items: stretch;
  min-height: ${({ $size }) => segmentSizeStyles[$size].groupHeight};
  ${({ $layout, $fixedWidth }) => {
    if ($layout === "fixed") {
      return css`
        width: ${$fixedWidth ?? 0}px;
      `;
    }
    if ($layout === "expanded") {
      return css`
        width: 100%;
      `;
    }
    return css`
      max-width: 100%;
    `;
  }}
  /* intrinsic 은 min-content(min-width:auto)를 유지 — flex 부모(예: 검색바 옆 한 행)에서 세그먼트
     아래로 줄어들어 버튼이 트랙 밖으로 삐져나오는 것을 막는다. expanded/fixed 만 0 까지 축소. */
  ${({ $layout }) =>
    $layout !== "intrinsic" &&
    css`
      min-width: 0;
    `}
  gap: 1px;
  padding: 1px;
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background-color: ${({ theme }) => theme.color.bg.surface};
`;

/**
 * ContentSwitcher는 action이 아니라 단일 선택 상태를 표현한다. 따라서 primary/secondary action
 * fill 대신 bg.selected state layer를 사용한다(SWD-647). 선택되지 않은 세그먼트는 muted →
 * default, 선택된 세그먼트는 selected → selectedHover → selectedActive로 진행한다.
 */
function buildSegmentSurface(theme: DefaultTheme, selected: boolean): InteractiveSurfaceSpec {
  return {
    rest: {
      background: selected ? theme.color.bg.selected : "transparent",
      border: "transparent",
      foreground: selected ? theme.color.text.strong : theme.color.text.muted,
    },
    hover: {
      background: selected ? theme.color.bg.selectedHover : theme.color.bg.muted,
      border: "transparent",
      foreground: selected ? theme.color.text.strong : theme.color.text.default,
    },
    active: {
      background: selected ? theme.color.bg.selectedActive : theme.color.bg.surfaceRaisedHigh,
      border: "transparent",
      foreground: selected ? theme.color.text.strong : theme.color.text.default,
    },
    disabled: {
      background: selected ? theme.color.bg.control.disabled : "transparent",
      border: "transparent",
      foreground: theme.color.text.disabled,
    },
  };
}

const Segment = styled.button<{
  $selected: boolean;
  $size: ContentSwitcherSize;
  $layout: LayoutWidth["type"];
  $iconOnly: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $size }) => segmentSizeStyles[$size].gap};
  /* intrinsic: 라벨 내용 폭(자르지 않음). expanded/fixed: 세그먼트 균등 분할(1 1 0). */
  flex: ${({ $layout }) => ($layout === "intrinsic" ? "0 0 auto" : "1 1 0")};
  /* intrinsic에서 selected regular↔bold 전환이 세그먼트 경계와 그룹 outer width를 움직이지 않게
     기존 label 요소의 숨은 ::after가 bold metric을 선점한다. DOM/접근성 텍스트는 복제하지 않고,
     expanded/fixed는 폭이 이미 외부 계약으로 고정되므로 이 metric guard를 적용하지 않는다. */
  ${({ $layout, theme }) =>
    $layout === "intrinsic" &&
    css`
      > [data-c6-content-switcher-label]::after {
        content: attr(data-c6-content-switcher-label);
        display: block;
        height: 0;
        overflow: hidden;
        visibility: hidden;
        font-weight: ${theme.typography.weight.bold};
        white-space: nowrap;
        pointer-events: none;
        user-select: none;
      }
    `}
  ${({ $layout }) =>
    $layout !== "intrinsic" &&
    css`
      min-width: 0;
    `}
  ${({ $layout }) =>
    $layout === "fixed" &&
    css`
      overflow: hidden;
    `}
  padding: 0 ${({ $size }) => segmentSizeStyles[$size].paddingX};
  /* 아이콘 전용(보이는 라벨 없음) 세그먼트는 정방형. flex row 아이템은 폭이 콘텐츠(아이콘)로,
     높이가 stretch 로 각각 정해져 aspect-ratio 가 무시되므로 — 폭을 트랙 안쪽 높이에 직접
     맞춘다. 트랙 안쪽 높이 = groupHeight(border-box) − 위아래 border(1px×2) − padding(1px×2).
     stretch 된 세그먼트 높이와 같은 값이라 정사각형이 된다. intrinsic 폭 모드에서만 —
     expanded/fixed 는 세그먼트가 폭을 채우는 게 목적이라 정방형을 적용하지 않는다. */
  ${({ $iconOnly, $layout, $size }) =>
    $iconOnly &&
    $layout === "intrinsic" &&
    css`
      padding-left: 0;
      padding-right: 0;
      width: calc(${segmentSizeStyles[$size].groupHeight} - 4px);
    `}
  /* 세그먼트 면은 Group의 border 1px + padding 1px 안쪽에 놓인다. 바깥과 같은 radius를
     반복하지 않고 실제 inset만큼 빼 두 곡선의 중심을 맞춘다. 0 하한은 consumer가 control
     radius를 2px보다 작게 재정의해도 유효한 반경을 보장한다. */
  border-radius: max(
    0px,
    calc(${({ theme }) => theme.shape.radius.control} - 2px)
  );
  border: 1px solid;
  font: inherit;
  font-size: ${({ $size }) => segmentSizeStyles[$size].fontSize};
  line-height: ${({ $size }) => inlineElementTypography[$size].lineHeight};
  font-weight: ${({ $selected, theme }) =>
    $selected ? theme.typography.weight.bold : theme.typography.weight.regular};

  ${({ $selected, theme }) => interactiveSurfaceCss(buildSegmentSurface(theme, $selected))}

  /* disabledTooltip이 있는 항목은 사유를 키보드로 발견할 수 있게 native disabled 대신
     aria-disabled를 쓴다. interactiveSurfaceCss의 공용 native 상태 뒤에서 같은 disabled
     layer를 적용해 hover/active를 막되 focus ring은 유지한다. &&는 공용 hover selector와
     specificity를 맞춰 뒤의 disabled layer가 이기게 한다. */
  &&[aria-disabled="true"] {
    background-color: ${({ $selected, theme }) =>
      $selected ? theme.color.bg.control.disabled : "transparent"};
    border-color: transparent;
    color: ${({ theme }) => theme.color.text.disabled};
    cursor: not-allowed;
  }

  /* 트랙 안쪽에서 이웃 세그먼트를 가리지 않도록 기존 inset focus 위치를 유지한다. ring의
     색·두께·형태는 interactiveSurfaceCss → controlFocusRing이 소유한다. */
  &:focus-visible {
    outline-offset: -2px;
  }
`;

// 아이콘(1em svg) 슬롯. 색은 세그먼트 text 색을 그대로 상속(currentColor), em-basis는 IconButton과
// 같은 primary icon scale을 쓴다. 아이콘은 장식이므로 aria-hidden — 이름은 label/ariaLabel이 소유.
const IconSlot = styled.span<{ $size: ContentSwitcherSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font-size: ${({ $size }) => inlineElementIconSize[$size]};
  line-height: 0;
`;

// 옵션별 기능/disabled 사유와 아이콘 전용 세그먼트 이름을 c6 Tooltip 으로 노출한다(Modal/Video
// 툴바와 hover 룩앤필 일치 — native title 대비 스타일·타이밍 일관 + 키보드 focus 에서도 표시). Tooltip 은 트리거를
// inline-flex span(Wrapper)으로 감싸므로, 그 Wrapper 를 세그먼트 대신 Group 의 flex 아이템으로 정렬한다:
// align-items:stretch 로 안쪽 버튼이 트랙 높이를 채우게 하고(Wrapper 기본 center override — && 로 특이도
// 확보), expanded/fixed 에선 flex:1 1 0 으로 등폭 분할에 참여시킨다.
const SegmentTooltip = styled(Tooltip)<{ $fill: boolean }>`
  && {
    align-items: stretch;
    ${({ $fill }) =>
      $fill &&
      css`
        flex: 1 1 0;
        min-width: 0;
      `}
  }
`;

// 세그먼트에 보이는 텍스트 라벨이 있는지. label 유무가 렌더(텍스트 vs 아이콘 전용)와 접근성
// 이름 소스(버튼 텍스트 vs ariaLabel)를 함께 가른다.
const hasVisibleLabel = (option: ContentSwitcherOption) =>
  option.label != null && option.label !== "";

const suppressActivationEvent = (event: SyntheticEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

const suppressActivationKey = (event: KeyboardEvent<HTMLButtonElement>) => {
  if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
    suppressActivationEvent(event);
  }
};

export function ContentSwitcher({
  label,
  options,
  value,
  onValueChange,
  size = "md",
  width = { type: "intrinsic" },
  tooltipPlacement = "bottom",
  disabled = false,
  className,
  style,
}: ContentSwitcherProps) {
  const handleSelect = useCallback(
    (option: ContentSwitcherOption) => {
      if (disabled || option.disabled) {
        return;
      }
      onValueChange(option.value);
    },
    [disabled, onValueChange],
  );

  const layout = width.type;
  const fixedWidth = width.type === "fixed" ? width.value : null;

  // 접근성 이름이 없는 세그먼트(아이콘 전용인데 ariaLabel 없음, 혹은 label·icon 둘 다 없음)를
  // 개발 중 인스턴스당 1회 경고 — icon-only IconButton 의 accessible-name 검사와 동일 패턴.
  const unnamed = options.filter((option) => !hasVisibleLabel(option) && option.ariaLabel == null);
  useDevWarnOnce(
    unnamed.length > 0,
    `[c6] <ContentSwitcher> segment(s) ${unnamed.map((option) => `"${option.value}"`).join(", ")} ` +
      "have no accessible name. Provide a `label`, or an `ariaLabel` for icon-only segments.",
  );

  return (
    <Group
      role="group"
      aria-label={label}
      className={className}
      style={style}
      $size={size}
      $layout={layout}
      $fixedWidth={fixedWidth}
    >
      {options.map((option) => {
        const selected = option.value === value;
        const hasIcon = option.icon != null;
        const hasLabel = hasVisibleLabel(option);
        // 접근성 이름: 보이는 라벨이 있으면 버튼 텍스트가 이름을 전달하므로 aria-label 을 얹지
        // 않는다. 아이콘 전용이면 ariaLabel 이 유일한 이름 — 아래 c6 Tooltip 으로 hover/focus 힌트로도 노출.
        const ariaLabel = hasLabel ? undefined : option.ariaLabel;
        const segmentDisabled = disabled || option.disabled === true;
        // TooltipIconButton과 같은 우선순위: disabled 사유가 있으면 기능 설명을 대체하고, 명시 tooltip이
        // 없을 때만 icon-only ariaLabel을 시각 힌트로 재사용한다.
        const configuredTooltip =
          segmentDisabled && isRenderableNode(option.disabledTooltip)
            ? option.disabledTooltip
            : option.tooltip;
        const hasConfiguredTooltip = isRenderableNode(configuredTooltip);
        const tooltipContent = hasConfiguredTooltip
          ? configuredTooltip
          : !hasLabel
            ? option.ariaLabel
            : undefined;
        const showTooltip = isRenderableNode(tooltipContent);
        const textVariant =
          layout !== "fixed"
            ? segmentTextVariant
            : hasConfiguredTooltip
              ? segmentEllipsisTextVariant
              : segmentTruncateTextVariant;
        // 명시 Tooltip이 있는 disabled 세그먼트는 native disabled 대신 aria-disabled로 둔다.
        // native disabled는 focus를 받지 않아 비활성 사유를 키보드/AT가 발견할 수 없기 때문이다.
        // Tooltip이 없는 기존 disabled 경로는 native semantics를 그대로 유지한다.
        const focusableDisabled = segmentDisabled && hasConfiguredTooltip;
        const segment = (
          <Segment
            key={option.value}
            type="button"
            aria-pressed={selected}
            aria-label={ariaLabel}
            aria-disabled={focusableDisabled || undefined}
            $selected={selected}
            $size={size}
            $layout={layout}
            $iconOnly={hasIcon && !hasLabel}
            disabled={segmentDisabled && !focusableDisabled}
            // React bubble handlers run from the root delegation target, after native listeners on
            // intermediate ancestors. Capture guards stop disabled activation before target/bubble,
            // including pointer release handlers commonly used by rows and drag surfaces.
            onPointerDownCapture={focusableDisabled ? suppressActivationEvent : undefined}
            onPointerUpCapture={focusableDisabled ? suppressActivationEvent : undefined}
            onMouseDownCapture={focusableDisabled ? suppressActivationEvent : undefined}
            onMouseUpCapture={focusableDisabled ? suppressActivationEvent : undefined}
            onKeyDownCapture={focusableDisabled ? suppressActivationKey : undefined}
            onKeyUpCapture={focusableDisabled ? suppressActivationKey : undefined}
            onClickCapture={focusableDisabled ? suppressActivationEvent : undefined}
            onClick={() => handleSelect(option)}
          >
            {hasIcon ? (
              <IconSlot $size={size} aria-hidden>
                {option.icon}
              </IconSlot>
            ) : null}
            {hasLabel ? (
              <Text
                variant={textVariant}
                data-c6-content-switcher-label={layout === "intrinsic" ? option.label : undefined}
              >
                {option.label}
              </Text>
            ) : null}
          </Segment>
        );
        // icon-only fallback은 내용이 곧 aria-label이라 중복 describe하지 않는다. 명시 tooltip과
        // disabledTooltip은 기능/사유 설명이므로 aria-describedby 경로를 유지한다.
        return showTooltip ? (
          <SegmentTooltip
            key={option.value}
            content={tooltipContent}
            placement={tooltipPlacement}
            describeTrigger={hasConfiguredTooltip}
            $fill={layout !== "intrinsic"}
          >
            {segment}
          </SegmentTooltip>
        ) : (
          segment
        );
      })}
    </Group>
  );
}

ContentSwitcher.displayName = "ContentSwitcher";
